import type {
  Account,
  Assumptions,
  ContributionRule,
  Scenario,
  ScenarioMeta,
} from '../types';
import type { SeedData } from './seed';

// ────────────────────────────────────────────────────────────────────────────
// First-run onboarding builder.
//
// The welcome screen collects only what the app cannot infer and must have
// to run: an age, a filing status, and (optionally) a salary. Everything
// else — balances, mortgage, spend, contributions — is entered from the
// same UIs a returning user would use (Update, Accounts, Assumptions), so
// the first-time flow doesn't invent a shape for the user's money.
//
// Why the visitor sees the standard 6-account catalog on first run:
//   HYSA · Checking · 401(k) · Taxable Brokerage · Home · Mortgage
// The Update page already knows how to prefill each account with its prior
// balance and let the user type over it — that's the "fill out a profile
// snapshot" primitive. Onboarding materializes the same catalog with zero
// balances (no starter snapshot) and hands off to that screen; the user
// enters their own numbers there. Accounts they don't have can be left at
// zero, unchecked on save, or removed from the Accounts page later.
//
// This is deliberately a separate builder from `buildSeed()`. The legacy
// seed carries illustrative placeholders (age 30, $200K net worth, an
// imaginary 401(k) balance) that were fine for dev but jarring for a first-
// time visitor. The onboarding path materializes ONLY what the visitor
// actually told us; every balance starts at zero until they say otherwise.
// ────────────────────────────────────────────────────────────────────────────

/**
 * Minimal input set from the welcome-screen form.
 *
 * `displayName`, `currentAge`, and `filingStatus` are required — the UI
 * needs an owner label, the projection needs an age, and the tax engine
 * needs a filing status. Salary is optional; a visitor who isn't working
 * (retired, between jobs, exploring) can skip it.
 *
 * Retirement age is deliberately absent: it defaults to 65 in the seed so
 * the tax engine has a number, but the welcome screen doesn't ask — Cairn
 * models arbitrary financial situations, not only retirement plans. A user
 * who wants to set a retirement date does so from Assumptions later.
 */
export interface OnboardingInput {
  /** Short display name for the profile owner ("Alex", "The Smiths"). Shown
   *  on the Dashboard header; not read by the engine. Trimmed on write. */
  displayName: string;
  /** Visitor's current age in whole years. Clamped to [0, 120]. */
  currentAge: number;
  /** Filing status — drives the tax engine's brackets + ACA calc. */
  filingStatus: 'single' | 'married';
  /** Optional gross annual salary. 0 / undefined = "not working" (fine for
   *  a retiree, career break, or someone just modeling other cashflows). */
  grossAnnualSalary?: number;
}

/**
 * Compose a first-run profile from an `OnboardingInput`. The output is a
 * fully-valid `SeedData` — the caller just wraps it in a `DbExport` and
 * writes it to storage.
 *
 * Design notes:
 *
 * - Account catalog matches `buildSeed()` (HYSA, Checking, 401(k), Taxable
 *   Brokerage, Home, Mortgage) so the visitor can enter balances on the
 *   Update page immediately after onboarding. `checking` has `bucket: null`
 *   — it's a transactional float, not part of HYSA math.
 * - No starter snapshot. `latestBalances()` returns `{}` for a fresh
 *   profile, which the engine handles cleanly. The visitor's first act on
 *   the Update page writes the first snapshot with their real numbers.
 * - Baseline scenario has zero events. The visitor authors their first
 *   life event from the Scenarios page.
 * - `mortgageMonthly*`, `monthlyHouseholdSpend`, `healthcareAnnual`, and
 *   `homeCostBasis` are all 0. The projection engine handles this fine
 *   (zero mortgage payoff, zero spend during retirement is unrealistic but
 *   deliberately visible — the visitor will notice and refine).
 * - `targetRetirementAge` defaults to 65 — a placeholder so the tax
 *   engine's retirement math has a number, not a claim about the visitor.
 *   Not surfaced on the welcome form.
 * - Real-return defaults (6% stock, 1% HYSA, 0.5% home) match `buildSeed()`
 *   so the numbers stay comparable across the two entry points.
 */
export function buildOnboardingSeed(input: OnboardingInput): SeedData {
  const now = new Date().toISOString();

  const currentAge = clamp(Math.round(input.currentAge || 0), 0, 120);
  const salary = Math.max(0, Math.round(input.grossAnnualSalary ?? 0));
  // Trim + hard-cap the display name. Empty is legitimate; the UI falls
  // back to "Cairn" when the field is blank.
  const displayName = (input.displayName ?? '').trim().slice(0, 60);

  const accounts: Account[] = [
    mk('hysa', 'HYSA', 'hysa', 'hysa', 0, now),
    mk('checking', 'Checking', 'checking', null, 1, now),
    mk('retirement_401k', '401(k)', 'retirement', 'retirement', 2, now),
    mk('taxable_brokerage', 'Taxable Brokerage', 'taxable', 'taxable', 3, now),
    mk('home', 'Home', 'home', null, 4, now),
    mk('mortgage', 'Mortgage', 'mortgage', null, 5, now),
  ];

  const assumptions: Assumptions = {
    id: 'singleton',
    displayName,
    currentAge,
    // Placeholder so the tax engine has a retirement horizon to compute
    // against. The visitor can refine or replace this from Assumptions.
    targetRetirementAge: Math.max(currentAge, 65),
    startingCalendarYear: new Date().getFullYear(),
    annualContribution401k: 0,
    annualContributionTaxable: 0,
    annualContributionHysa: 0,
    grossAnnualSalary: salary,
    compensation: {
      baseAnnual: salary,
      bonusAnnual: 0,
      bonusMonth: 2 as const,
    },
    contributionRules: [] satisfies ContributionRule[],
    stockRealReturn: 0.06,
    hysaRealReturn: 0.01,
    homeRealAppreciation: 0.005,
    swr: 0.04,
    inflation: 0.03,
    mortgageAnnualRate: 0,
    mortgageMonthlyPI: 0,
    mortgageMonthlyEscrow: 0,
    monthlyHouseholdSpend: 0,
    healthcareAnnual: 0,
    filingStatus: input.filingStatus,
    householdSize: input.filingStatus === 'married' ? 2 : 1,
    ltcgRate: 0.15,
    ordinaryRate: 0.22,
    stateTaxRate: 0.05,
    earlyWithdrawalPenalty: 0.1,
    homeCostBasis: 0,
    updatedAt: now,
  };

  const baseline: Scenario = {
    id: 'baseline',
    name: 'Baseline',
    description: 'Your plan of record',
    icon: '🗻',
    color: '#3b54e8',
    order: 0,
    archived: false,
    events: [],
    createdAt: now,
  };

  const meta: ScenarioMeta = {
    id: 'singleton',
    activeScenarioId: 'baseline',
    compareScenarioIds: [],
    updatedAt: now,
  };

  return {
    accounts,
    snapshots: [],
    assumptions,
    purchases: [],
    scenarios: [baseline],
    meta,
  };
}

function mk(
  id: string,
  name: string,
  kind: Account['kind'],
  bucket: Account['bucket'],
  order: number,
  now: string,
): Account {
  return {
    id,
    name,
    kind,
    bucket,
    realReturnOverride: null,
    order,
    archived: false,
    createdAt: now,
  };
}

function clamp(v: number, lo: number, hi: number): number {
  if (!Number.isFinite(v)) return lo;
  if (v < lo) return lo;
  if (v > hi) return hi;
  return v;
}
